import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPlus, LuPencil, LuTrash } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    DeleteConfirmModal,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

const initialFilters = {
    name: "",
    master_cash_category_id: "",
};

const MasterCashData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("master-cash.edit") || hasPermission("master-cash.delete");
    const table = useDataTable(
        "master-cash/data",
        "master-cash/{id}/delete",
        {},
        initialFilters,
    );

    const { data: categories } = useFetchData("select/master-cash-category");

    const filterLabels = {
        name: { label: "Nama" },
        master_cash_category_id: {
            label: "Kategori",
            getValueLabel: (val) => {
                const item = categories?.find(
                    (c) => String(c.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama", skeletonWidth: "w-40" },
        { name: "Kategori", skeletonWidth: "w-32" },
        { name: "Akun Jurnal", skeletonWidth: "w-48" },
        { name: "Keterangan", skeletonWidth: "w-48" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Master Cash."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Master Cash"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Keuangan", href: "/finance" },
                        { label: "Master Cash" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("master-cash.create") && (
                        <Button
                            as={Link}
                            to="/master-cash/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Data
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{ categories: categories || [] }}
            />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <DeleteConfirmModal table={table} />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Formula" // Copied from MaterialFormula, adjusting to Master Cash
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada data Master Cash."
                            }
                        />
                    ) : (
                        <Table aria-label="Master Cash Table" shadow="none">
                            <TableHeader>
                                <TableColumn>No.</TableColumn>
                                <TableColumn>Nama</TableColumn>
                                <TableColumn>Kategori</TableColumn>
                                <TableColumn>Akun Jurnal</TableColumn>
                                <TableColumn>Keterangan</TableColumn>
                                {hasAnyAction && <TableColumn align="end">Aksi</TableColumn>}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.name}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.category?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.journal_account_id
                                                    ? item.journal_account
                                                        ? `${item.journal_account.account_code} - ${item.journal_account.name}`
                                                        : item.journal_account_id
                                                    : "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="xs"
                                                variant="caption"
                                                className="line-clamp-1"
                                            >
                                                {item.description || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        {hasAnyAction && (
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    {hasPermission("master-cash.edit") && (
                                                        <Tooltip content="Edit">
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="light"
                                                                as={Link}
                                                                to={`/master-cash/${item.id}/edit`}
                                                            >
                                                                <LuPencil
                                                                    size={14}
                                                                    className="text-default-500"
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {hasPermission("master-cash.delete") && (
                                                        <Tooltip content="Hapus">
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="light"
                                                                color="danger"
                                                                onPress={() =>
                                                                    table.deleteItem(
                                                                        item.id,
                                                                    )
                                                                }
                                                            >
                                                                <LuTrash size={14} />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default MasterCashData;
